import React, { useEffect, useState, useMemo, useCallback } from "react";
import NumberFormat from "react-number-format";
import styled from "styled-components";
import media from "styled-media-query";
import sort from "fast-sort";
import _ from "lodash";
import { Animate } from "react-show";

import {
  formatDate,
  formatDateToDate,
  toaster,
} from "src/helpers/UtilityHelper";
import { isReservation } from "src/helpers/OrderHelper";

import { OrderEntity } from "src/firebase/db/Orders";
import { BakedBreadEntity } from "src/firebase/db/BakedBreads";
import { UserEntity } from "src/firebase/db/Users";
import StyleConfig from "src/constants/StyleConfig";

import CheckBox from "src/components/atoms/form/CheckBox";

interface Props {
  order: OrderEntity;
  user: UserEntity;
  bakedBreads: BakedBreadEntity[];
  prevOrders: OrderEntity[];
  filterByStatus: string;
  filterByPickup: boolean;
  filterByReserved: boolean;
  onUpdateIsConfirmed: (id: string, isConfirmed: boolean) => void;
  onUpdateIsCompleted: (id: string, isCompleted: boolean) => void;
}

export const OrderListItem = ({ ...props }: Props) => {
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  const {
    order,
    user,
    prevOrders,
    bakedBreads,
    filterByStatus,
    filterByPickup,
    filterByReserved,
    onUpdateIsConfirmed,
    onUpdateIsCompleted,
  } = props;

  const {
    id,
    created_at,
    last_pickup_time,
    order_number,
    amount,
    items,
    options,
    is_confirmed,
    is_completed,
    status,
    user_request_at_pickup,
  } = order;

  /**
   * 初期表示：チェックボックスの値をstateに突っ込む
   * 更新後に画面を再描画する方が実装的にはスッキリするが、API叩く回数が増えそうなのでこちらで対応
   */
  useEffect(() => {
    setIsConfirmed(is_confirmed || false);
    setIsCompleted(is_completed || false);
  }, [is_confirmed, is_completed]);

  // 予約商品か否か
  const isReservationOrder = useMemo(() => isReservation(order, bakedBreads), [
    order,
    bakedBreads,
  ]);

  const lastPickupTime = useMemo(() => {
    // 最も遅い最終受取時刻を表示させる
    const results = sort(items).by([
      {
        desc: (v) => {
          const { order_bread } = v;
          return formatDateToDate(order_bread.last_pickup_time).getTime();
        },
      },
    ]);
    const item = results[0];
    if (!item) {
      return "";
    }
    if (!item.order_bread.last_pickup_time) {
      return isReservationOrder ? formatDate(last_pickup_time, "MM月DD日") : "";
    }
    return formatDate(item.order_bread.last_pickup_time, "HH:mm");
  }, [items, last_pickup_time, isReservationOrder]);

  // 注文時刻
  const createdAt = useMemo(
    () =>
      formatDate(created_at, `${isReservationOrder ? "MM月DD日" : "HH:mm"}`),
    [isReservationOrder, created_at]
  );

  // 注文者名
  const userName = useMemo(() => `${user ? `${user.name}` : "名無し"}`, [user]);

  // 注文回数
  const prevOrderCount = useMemo(() => `${prevOrders.length}`, [prevOrders]);

  // 注文個数合計
  const total = useMemo(() => _.sum(items.map((item) => item.quantity)), [
    items,
  ]);

  // 複数購入で重複した明細を合算し、個数,名称昇順でソートして返す
  const sortedItems = useMemo(() => {
    const totalItems = _(items)
      .groupBy((item) => item.order_bread.bread_id)
      .map((item) => ({
        id: item[0].id,
        name: item[0].name,
        quantity: _.sumBy(item, "quantity"),
        price: _.sumBy(item, "price"),
      }))
      .value();
    return sort(totalItems).by([
      { asc: ({ quantity }) => quantity },
      { asc: ({ name }) => name },
    ]);
  }, [items]);

  const showToaster = useCallback(() => {
    // toaster('success', `${label}を更新しました。${order_number}:　${!checked ? '□ → ✔︎': '✔︎ → □'}`);
    toaster("success", "リストを更新しました。");
  }, []);

  const wrapOnUpdateIsConfirmed = useCallback(() => {
    onUpdateIsConfirmed(id, !isConfirmed);

    // ✅準備済 ✅受取済 の時に ⬜️準備済に変更する場合、同時に ⬜️受取済にしたい
    if (isConfirmed && isCompleted) {
      setIsCompleted(false);
      onUpdateIsCompleted(id, false);
    }
    setIsConfirmed(!isConfirmed);
    showToaster();
  }, [
    id,
    isConfirmed,
    isCompleted,
    onUpdateIsConfirmed,
    onUpdateIsCompleted,
    showToaster,
  ]);

  const wrapOnUpdateIsCompleted = useCallback(() => {
    onUpdateIsCompleted(id, !isCompleted);

    // ⬜️準備済 ⬜️受取済 の時に ✅受取済に変更する場合、同時に ✅準備済にしたい
    if (!isCompleted && !isConfirmed) {
      setIsConfirmed(true);
      onUpdateIsConfirmed(id, true);
    }
    setIsCompleted(!isCompleted);
    showToaster();
  }, [
    id,
    isConfirmed,
    isCompleted,
    onUpdateIsConfirmed,
    onUpdateIsCompleted,
    showToaster,
  ]);

  const isVisible = useMemo(() => {
    if (!filterByPickup && !isReservationOrder) {
      // 取り置き商品のチェックがない場合
      return false;
    }
    if (!filterByReserved && isReservationOrder) {
      // 予約商品のチェックがない場合
      return false;
    }
    switch (filterByStatus) {
      case "準備する商品を表示":
        return !isConfirmed;
      case "準備済の商品を表示":
        return isConfirmed;
      case "受渡済の商品を表示":
        return isCompleted;
      case "すべての商品を表示":
        return true;
      default:
        return true;
    }
  }, [
    filterByStatus,
    filterByPickup,
    filterByReserved,
    isConfirmed,
    isCompleted,
    isReservationOrder,
  ]);

  if (status === "error") {
    return null;
  }

  return (
    <Animate
      show={isVisible}
      // この辺trueにするとメモリリークが発生するっぽい
      // transitionOnMount
      // preMount
      // stayMounted
      duration={500}
      style={{
        height: "auto",
        background: "white",
      }}
      start={{
        opacity: 0,
        height: 0,
      }}
      enter={{
        opacity: 1,
        height: "auto",
      }}
      leave={{
        opacity: 0,
        height: 0,
      }}
    >
      <div>
        <Styled isReservation={isReservationOrder}>
          <div
            className={`order ${isReservationOrder ? "is-reservation" : ""}`}
          >
            <p className="order-number">
              <span>受取番号</span>
              <span className="separator">：</span>
              <span>{order_number}</span>
            </p>
            <p className="order-user">
              <span>注文者</span>
              <span className="separator">：</span>
              <span>
                <b>{userName || "名無し"}</b>
                <small>さま（{prevOrderCount}回目）</small>
              </span>
            </p>
            <div className="order-detail">
              <div className="order-detail-timestamp">
                <span>{isReservationOrder ? "注文日" : "注文時刻"}</span>
                <span className="separator">：</span>
                <span>{createdAt}</span>
              </div>
              {lastPickupTime && (
                <div className="order-detail-timestamp">
                  <span>
                    {isReservationOrder ? "受け取り予定日" : "最終受取時刻"}
                  </span>
                  <span className="separator">：</span>
                  <span>{lastPickupTime}</span>
                </div>
              )}
              <div className="order-detail-amount">
                <span>売上（税込）</span>
                <span className="separator">：</span>
                <NumberFormat
                  value={amount}
                  displayType={"text"}
                  thousandSeparator={true}
                  suffix=" 円"
                />
              </div>
              <div className="order-detail-bags">
                <span>レジ袋</span>
                <span className="separator">：</span>
                <span>
                  {options.length === 0 ? (
                    <span className="bag">なし</span>
                  ) : (
                    options.map(({ name, quantity }) => (
                      <span
                        className="bag"
                        key={`${order_number}|bags|${name}`}
                      >
                        {name}サイズ {quantity}枚
                      </span>
                    ))
                  )}
                </span>
              </div>
              {user_request_at_pickup && (
                <div className="order-detail-request">
                  <span>受け取り時のご要望</span>
                  <span className="separator">：</span>
                  <span>{user_request_at_pickup}</span>
                </div>
              )}
            </div>
            <div className="order-actions">
              <CheckBox
                children={"準備済"}
                isChecked={isConfirmed}
                onChange={() => wrapOnUpdateIsConfirmed()}
              />
              <CheckBox
                children={"受渡済"}
                isChecked={isCompleted}
                onChange={() => wrapOnUpdateIsCompleted()}
              />
            </div>
          </div>
          <div className="items">
            <div className="items-list">
              {sortedItems.map((item) => (
                <p
                  className="items-list-item"
                  key={`${order_number}|${item.id}`}
                >
                  <span>{item.name}</span>
                  <span>{item.quantity}個</span>
                </p>
              ))}
              <p className="items-list-item is-total">
                <span>合計注文個数</span>
                <NumberFormat
                  value={total}
                  displayType={"text"}
                  thousandSeparator={true}
                  suffix=" 個"
                />
              </p>
            </div>
          </div>
        </Styled>
      </div>
    </Animate>
  );
};

type StyledProps = {
  isReservation: boolean | undefined;
};

const Styled = styled.div<StyledProps>`
  background-color: #fff;
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  padding: ${StyleConfig.HELPER.M16};
  border-radius: 4px;
  margin-bottom: 20px;
  &:last-child {
    margin-bottom: 40px;
  }

  ${media.greaterThan("medium")`
    flex-wrap: nowrap;
  `};

  .separator {
    display: none;
  }

  .order {
    flex: 100%;
    display: flex;
    flex-wrap: wrap;
    ${media.greaterThan("medium")`
      flex: 55%;
    `};
    .order-number {
      width: 100%;
      font-size: ${StyleConfig.TYPO.LV2};
      font-weight: bold;
      color: ${StyleConfig.COLOR.PRIMARY};
      text-align: center;
      margin-bottom: ${StyleConfig.HELPER.M4};
      order: 1;

      .separator {
        display: inline;
      }

      ${media.greaterThan("medium")`
        text-align: left;
        display: inline-flex;
        width: 100%;
        text-align: right;
        span {
          flex: 1;
          :last-child {
            text-align: left;
            max-width: 200px;
            min-width: 200px;
            margin-left: 20px;
            font-weight: bold;
          }
          &.separator {
            display: none;
          }
        }
      `};
    }
    .order-user {
      width: 100%;
      font-size: ${StyleConfig.TYPO.TEXT};
      color: ${StyleConfig.COLOR.PRIMARY};
      text-align: center;
      margin-bottom: ${StyleConfig.HELPER.M20};
      order: 1;

      .separator {
        display: inline;
      }

      span {
        :last-child {
          color: ${StyleConfig.COLOR.TEXT_COLOR};
        }
        b {
          margin-right: 4px;
        }
      }

      ${media.greaterThan("medium")`
        margin-bottom: ${StyleConfig.HELPER.M12} !important;
        text-align: left;
        display: inline-flex;
        width: 100%;
        text-align: right;
        span {
          flex: 1;
          :last-child {
            text-align: left;
            max-width: 200px;
            min-width: 200px;
            margin-left: 20px;
          }
          &.separator {
            display: none;
          }
        }
      `};
    }
    .order-detail {
      width: 100%;
      order: 2;
      ${media.greaterThan("medium")`
        order: 1;
      `};

      .order-detail-timestamp,
      .order-detail-amount,
      .order-detail-bags {
        display: inline-flex;
        width: 100%;
        text-align: right;
        &:not(:last-child) {
          margin-bottom: 4px;
          /* ${media.greaterThan("medium")`
            margin-bottom: 4px;
          `} */
        }
        > span {
          flex: 1;
          :first-child {
            color: ${StyleConfig.COLOR.TEXT_GRAY};
          }
          :last-child {
            font-weight: bold;
            max-width: 100px;
            min-width: 100px;
            ${media.greaterThan("medium")`
              text-align: left;
              max-width: 200px;
              min-width: 200px;
              margin-left: 20px;
            `}
          }
        }
      }

      .order-detail-bags {
        > span {
          :last-child {
            display: inline-flex;
            flex-wrap: wrap;

            .bag {
              width: 100%;
            }
          }
        }
      }

      .order-detail-request {
        width: 100%;
        ${media.lessThan("medium")`
          padding-top: 24px;
          margin-top: 24px;
          border-top: 1px dotted #D2D2D2;
          text-align: justify;

          span {
            &:first-child {
              color: ${StyleConfig.COLOR.TEXT_GRAY};

              &::before {
                content: "（";
              }
              &::after {
                content: "）";
              }
            }
          }
        `}

        ${media.greaterThan("medium")`
          display: inline-flex;
          align-items: flex-start;
          text-align: right;
          &:not(:last-child) {
            margin-bottom: 4px;
          }
          > span {
            flex: 1;
            :first-child {
              color: ${StyleConfig.COLOR.TEXT_GRAY};
            }
            :last-child {
              font-weight: bold;
              text-align: left;
              max-width: 200px;
              min-width: 200px;
              margin-left: 20px;
            }
          }
        `}
      }
    }
    .order-actions {
      display: inline-flex;
      flex-wrap: wrap;
      width: 100%;
      margin-bottom: 24px;
      padding-bottom: 24px;
      font-size: 20px;
      border-bottom: 1px dotted ${StyleConfig.COLOR.INPUT_BORDER};
      order: 1;
      ${media.greaterThan("medium")`
        margin-top: 12px;
        margin-bottom: 0;
        padding-bottom: 0;
        font-size: 14px;
        border-bottom: none;
      `};
      > * {
        width: 50%;
        justify-content: center;
        ${media.greaterThan("medium")`
          width: 120px;
          justify-content: flex-end;
        `};
        input[type="checkbox"] {
          border: 1px solid ${StyleConfig.COLOR.INPUT_BORDER} !important;
        }
      }
    }
  }

  .items {
    width: 100%;
    flex: 100%;
    margin-top: ${StyleConfig.HELPER.M20};
    overflow: hidden;
    ${media.greaterThan("medium")`
      flex: 45%;
      margin-top: 0;
    `};

    .items-list {
      font-weight: bold;

      .items-list-item {
        display: inline-flex;
        justify-content: space-between;
        width: 100%;
        font-size: ${StyleConfig.TYPO.TEXT};

        ${media.greaterThan("medium")`
          justify-content: flex-end;
        `};

        &:not(:last-child) {
          margin-bottom: ${StyleConfig.HELPER.M4};
        }

        &.is-total {
          font-size: ${StyleConfig.TYPO.LV2};
          margin-top: ${StyleConfig.HELPER.M8};
        }

        span {
          &:first-child {
            text-align: left;
            ${media.greaterThan("medium")`
              padding-left: 30px;
              text-align: right;
              text-overflow: ellipsis;
              overflow: hidden;
              width: 100%;
              /* 改行を半角スペースに変換することで、1行にする */
              white-space: nowrap;
            `};
          }
          &:last-child {
            text-align: right;
            width: 100px;
            min-width: 100px;
          }
        }
      }
    }
  }

  ${({ isReservation }) => {
    if (isReservation) {
      return `
        background: ${StyleConfig.COLOR.GREEN_LIGHT};
        border: 1px solid ${StyleConfig.COLOR.GREEN};
        box-sizing: border-box;

        .order-number {
          color: ${StyleConfig.COLOR.GREEN} !important;
        }
        .order-user {
          span:first-child {
            color: ${StyleConfig.COLOR.GREEN} !important;
          }
        }
        input[type='checkbox'] {
          &:after {
            background-image: url(/images/icon/checked_g.svg);
          }
        }
      `;
    }
  }};
`;
export default OrderListItem;
